import * as Sentry from '@sentry/react'
import { type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface FallbackProps {
    error: Error
    resetError: () => void
}

function ErrorFallback({ error, resetError }: FallbackProps) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
                <div className="mb-4 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                        <svg
                            className="h-8 w-8 text-red-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Something went wrong
                    </h1>
                    <p className="mt-2 text-gray-600">
                        We've been notified of this issue and are working to fix
                        it.
                    </p>
                </div>

                {import.meta.env.DEV && (
                    <div className="mb-4 rounded-md bg-gray-100 p-4">
                        <p className="text-sm font-medium text-gray-700">
                            Error Details (Development Only):
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                            {error.message}
                        </p>
                        {error.stack && (
                            <details className="mt-2">
                                <summary className="cursor-pointer text-xs text-gray-500">
                                    Stack Trace
                                </summary>
                                <pre className="mt-2 overflow-auto text-xs text-gray-600">
                                    {error.stack}
                                </pre>
                            </details>
                        )}
                    </div>
                )}

                <div className="flex gap-3">
                    <Button
                        onClick={resetError}
                        className="flex-1"
                        variant="default"
                    >
                        Try Again
                    </Button>
                    <Button
                        onClick={() => (window.location.href = '/')}
                        className="flex-1"
                        variant="outline"
                    >
                        Go Home
                    </Button>
                </div>
            </div>
        </div>
    )
}

interface ErrorBoundaryProps {
    children: ReactNode
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
    return (
        <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => (
                <ErrorFallback error={error as Error} resetError={resetError} />
            )}
            showDialog={false}
        >
            {children}
        </Sentry.ErrorBoundary>
    )
}

export default ErrorBoundary
